import { useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/lib/api/endpoints";
import type {
  InvoiceSummary,
  OffPremiseGridEntry,
  InvoiceHistoryPage,
  RestaurantSettings,
} from "@/lib/api/types";
import { invalidateInvoiceMutation } from "@/shared/invalidation";
import { queryKeys, useApiQuery } from "@/shared/queries";
import { useInvoiceCommands } from "@/shared/useInvoiceCommands";
import { useErrorMessage } from "@/shared/errors";
import { usePrivateToast } from "@/shared/usePrivateToast";
import { resolveOpenInvoicePayload } from "../lib/openInvoicePayload";
import { STAFF_MEAL_BILL_NAME } from "../lib/staffMeal";
import { OffPremiseDialog } from "./OffPremiseDialog";

/**
 * THE BILLS AT NO TABLE ON THE FLOOR (F1) — read and started from one place,
 * so both floors (the till's `/pos` and the back office's live view) carry the
 * same Takeaway section with the same commands.
 */

/**
 * The live takeaway and delivery bills — OPEN, and since T8 (2026-10-06)
 * CLOSED-but-unpaid too.
 *
 * Both floors read `GET /restaurant/invoices/grid/off-premise`, which carries
 * the same plate and money facts a table's bill does and returns the bills in
 * the order the strip shows them: OPEN oldest first, then CLOSED oldest first.
 * It used to be the PAPER floor's read alone, with the older till drawing its
 * rows from the two channel LISTS — which only ever asked for OPEN bills, so a
 * closed delivery vanished from that till the moment its cheque printed while
 * the rider was still out (G8). One read for both floors is what makes the two
 * agree about what is live.
 *
 * Whenever that read fails — an API from before the route existed — the two
 * OPEN channel lists are used instead, and the bills are drawn from their
 * summaries (no plate states, so the tile says "Occupied" and owes the running
 * total; and no closed bills, which such an API cannot report anyway).
 */
export function useOffPremiseBills({
  enabled,
}: {
  enabled: boolean;
}): { bills: OffPremiseGridEntry[] } {
  const floor = useApiQuery<OffPremiseGridEntry[]>(
    [...queryKeys.offPremiseOpen, "grid"],
    endpoints.restaurant.gridOffPremise,
    {
      enabled,
      refetchOnWindowFocus: true,
      // A missing route is an older API, not a blip: fall back at once.
      retry: false,
    },
  );
  const useLists = enabled && floor.isError;
  const takeawayOpen = useApiQuery<InvoiceHistoryPage<InvoiceSummary>>(
    [...queryKeys.offPremiseOpen, "TAKEAWAY"],
    `${endpoints.restaurant.invoices}?channel=TAKEAWAY&status=OPEN&limit=50`,
    { enabled: useLists },
  );
  const deliveryOpen = useApiQuery<InvoiceHistoryPage<InvoiceSummary>>(
    [...queryKeys.offPremiseOpen, "DELIVERY"],
    `${endpoints.restaurant.invoices}?channel=DELIVERY&status=OPEN&limit=50`,
    { enabled: useLists },
  );
  const summaries = [
    ...(takeawayOpen.data?.rows ?? []),
    ...(deliveryOpen.data?.rows ?? []),
  ];
  return {
    bills: floor.data ?? summaries.map(summaryAsBill),
  };
}

/**
 * A list summary as a floor bill: no plate or money facts beyond its total.
 * The customer and platform ride along where the list carries them (C5); the
 * platform's NAME is not on a summary, so the tile shows no badge on this path.
 */
export function summaryAsBill(summary: InvoiceSummary): OffPremiseGridEntry {
  return {
    invoiceId: summary.id,
    dailySequence: summary.dailySequence,
    status: summary.status,
    version: 0,
    openedAt: summary.openedAt,
    runningTotal: summary.total,
    pendingItemCount: 0,
    hasPendingPaymentClaim: false,
    selfSeated: false,
    channel: summary.channel,
    customerName: summary.customerName ?? null,
    customerPhone: summary.customerPhone ?? null,
    aggregatorId: summary.aggregatorId ?? null,
    aggregatorName: null,
    platformOrderRef: summary.platformOrderRef ?? null,
    closedAt: summary.closedAt,
    // A list row does not say who the bag left with.
    handedTo: null,
  };
}

/**
 * Starting a bill at no table: the channel's dialog, then `openInvoice` with no
 * table, then the bill. The same command the till's two buttons always sent.
 */
export function useOffPremiseStart({ enabled }: { enabled: boolean }): {
  begin: (channel: "TAKEAWAY" | "DELIVERY") => void;
  /**
   * D154 — the crew's meal: a takeaway under `STAFF_MEAL_BILL_NAME`, opened at
   * once (no dialog: nothing in it is a customer's) and handed to the register,
   * whose menu adds the dishes and whose banner fires and comps them.
   */
  beginStaffMeal: () => void;
  /**
   * B6 · P24b (the design's `onNewDelivery` / `onNewPickup`): the PAPER
   * Delivery tab's new-order commands go STRAIGHT into the register — the
   * bill opens at once with no customer (the server allows that, B103; an own
   * delivery is refused only at fire and close, D-39) and the order panel asks
   * for the name, phone and address inline. Blank fee = the store's default,
   * direct (no platform; `ChargesDialog` corrects either while OPEN).
   */
  beginDirect: (channel: "TAKEAWAY" | "DELIVERY") => void;
  /** A start is in flight: the commands that start one are disabled. */
  isStarting: boolean;
  dialog: ReactNode;
} {
  const { dispatchCommand } = useInvoiceCommands();
  const navigate = useNavigate();
  const { locale = "ar" } = useParams();
  const queryClient = useQueryClient();
  const toast = usePrivateToast();
  const errorMessage = useErrorMessage();
  const [offPremise, setOffPremise] = useState<"TAKEAWAY" | "DELIVERY" | null>(
    null,
  );
  const [isStarting, setStarting] = useState(false);
  const settingsQuery = useApiQuery<RestaurantSettings>(
    queryKeys.settings,
    endpoints.restaurant.settings,
    { enabled },
  );

  const start = async (input: {
    customerName: string;
    customerPhone: string;
    deliveryAddress: string;
    deliveryFeeRaw: string;
    aggregatorId: string | null;
    platformOrderRef: string;
  }) => {
    if (!offPremise || isStarting) return;
    setStarting(true);
    try {
      const result = await dispatchCommand({
        type: "openInvoice",
        payload: resolveOpenInvoicePayload({
          // No table: `resolveOpenInvoicePayload` drops the key entirely on an
          // off-premise channel, which is what the server requires.
          tableId: "",
          channel: offPremise,
          ...input,
          tierTouched: false,
          tierId: null,
          guestCountRaw: "",
        }),
      });
      if (result.invoice) {
        navigate(`/${locale}/pos/invoices/${result.invoice.id}`);
      }
      await invalidateInvoiceMutation(queryClient, result.invoice?.id);
    } finally {
      setStarting(false);
      setOffPremise(null);
    }
  };

  const openAtOnce = async (channel: "TAKEAWAY" | "DELIVERY", customerName: string) => {
    if (isStarting) return;
    setStarting(true);
    try {
      const result = await dispatchCommand({
        type: "openInvoice",
        payload: resolveOpenInvoicePayload({
          tableId: "",
          channel,
          customerName,
          customerPhone: "",
          deliveryAddress: "",
          deliveryFeeRaw: "",
          aggregatorId: null,
          platformOrderRef: "",
          tierTouched: false,
          tierId: null,
          guestCountRaw: "",
        }),
      });
      if (result.invoice) {
        navigate(`/${locale}/pos/invoices/${result.invoice.id}`);
      }
      await invalidateInvoiceMutation(queryClient, result.invoice?.id);
    } catch (failure) {
      // A refused open (no shift, a closed day…) is said, not swallowed.
      toast.error(errorMessage(failure));
    } finally {
      setStarting(false);
    }
  };

  return {
    begin: setOffPremise,
    beginStaffMeal: () => void openAtOnce("TAKEAWAY", STAFF_MEAL_BILL_NAME),
    beginDirect: (channel) => void openAtOnce(channel, ""),
    isStarting,
    dialog: offPremise ? (
      <OffPremiseDialog
        channel={offPremise}
        defaultDeliveryFee={settingsQuery.data?.deliveryFeeDefault ?? null}
        isSubmitting={isStarting}
        onCancel={() => setOffPremise(null)}
        onConfirm={start}
      />
    ) : null,
  };
}
